import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Loader2, Send } from "lucide-react"
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "src/components/ui/sheet"
import { Button } from "src/components/ui/button"
import { Textarea } from "src/components/ui/textarea"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { CHANNEL_MAP } from "src/features/templates/constants"
import {
  fetchTemplates, previewTemplate, sendTemplateMessage,
} from "src/features/templates/api/templatesApi"
import { fetchPropertyListings } from "src/features/properties/api/propertyListingsApi"
import { useChannelProviders } from "src/features/integrations/hooks/useChannelProviders"
import { ProviderPicker } from "src/features/integrations/components/ProviderPicker"

// The category tabs offered in the drawer. "lead" uses the lead itself,
// "contact" the lead's linked contact, "property" a picked listing.
const TABS = [
  { value: "lead", label: "Lead" },
  { value: "contact", label: "Contact" },
  { value: "property", label: "Property" },
]

// Resolve the recipient + to_address for a category, given the lead and the
// (optionally) picked property. Returns null when the category can't be sent
// (e.g. Contact tab but the lead has no linked contact).
function resolveRecipient(category, lead, property, channel) {
  const addr = (rec) => (channel === "email" ? rec?.email : rec?.phone) || null

  if (category === "lead") {
    return { recipient: { type: "Lead", id: lead.id }, to_address: addr(lead), label: lead.name }
  }
  if (category === "contact") {
    if (!lead.contact) return null
    return { recipient: { type: "Contact", id: lead.contact.id }, to_address: addr(lead.contact), label: lead.contact.name }
  }
  if (category === "property") {
    if (!property) return null
    // A property listing is owned by a contact; messages go to the lead we're
    // working, so the address stays the lead's.
    return { recipient: { type: "PropertyListing", id: property.id }, to_address: addr(lead), label: property.title }
  }
  return null
}

function Tabs({ value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
      {TABS.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
            value === t.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

// channel = "whatsapp" | "sms" | "email"
export function SendMessageDrawer({ open, lead, channel, onClose }) {
  const { toast } = useToast()
  const channelMeta = CHANNEL_MAP[channel] || {}
  const ChannelIcon = channelMeta.icon

  const [category, setCategory] = useState("lead")
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [templateId, setTemplateId] = useState("")
  const [message, setMessage] = useState("")
  const [previewing, setPreviewing] = useState(false)

  // Property picker (only used on the Property tab).
  const [properties, setProperties] = useState([])
  const [propertyId, setPropertyId] = useState("")

  // Reset everything when (re)opened or the channel changes.
  useEffect(() => {
    if (!open) return
    setCategory("lead")
    setTemplateId("")
    setMessage("")
    setPropertyId("")
  }, [open, channel])

  // Load the active templates for the current channel + category tab.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setTemplatesLoading(true)
    setTemplateId("")
    setMessage("")
    fetchTemplates({ channel, category })
      .then((d) => {
        if (cancelled) return
        const active = (d.templates || []).filter((t) => t.status === "active")
        setTemplates(active)
      })
      .catch(() => !cancelled && setTemplates([]))
      .finally(() => !cancelled && setTemplatesLoading(false))
    return () => { cancelled = true }
  }, [open, channel, category])

  // Load property options when switching to the Property tab.
  useEffect(() => {
    if (!open || category !== "property") return
    let cancelled = false
    fetchPropertyListings({ per_page: 100, status: "active" })
      .then((d) => !cancelled && setProperties(d.listings || []))
      .catch(() => !cancelled && setProperties([]))
    return () => { cancelled = true }
  }, [open, category])

  const selectedProperty = useMemo(
    () => properties.find((p) => String(p.id) === String(propertyId)) || null,
    [properties, propertyId]
  )

  const resolved = lead ? resolveRecipient(category, lead, selectedProperty, channel) : null

  // Render the chosen template against the real recipient for the preview.
  const refreshPreview = useCallback(() => {
    if (!templateId || !resolved) { setMessage(""); return }
    setPreviewing(true)
    previewTemplate(templateId, resolved.recipient)
      .then((d) => setMessage(d.body || ""))
      .catch(() => toast({ variant: "destructive", title: "Couldn't render template" }))
      .finally(() => setPreviewing(false))
  }, [templateId, resolved?.recipient?.type, resolved?.recipient?.id]) // eslint-disable-line

  useEffect(() => { refreshPreview() }, [refreshPreview])

  // Email has no provider choice; whatsapp/sms may. The picker renders only when
  // 2+ providers are enabled for the channel.
  const providers = useChannelProviders(channel)

  const [sending, setSending] = useState(false)
  async function handleSend() {
    if (!templateId || !resolved) return
    if (!resolved.to_address) {
      toast({ variant: "destructive", title: `No ${channel === "email" ? "email" : "phone"} on file for this recipient` })
      return
    }
    setSending(true)
    try {
      const data = await sendTemplateMessage(templateId, {
        recipient: resolved.recipient,
        to_address: resolved.to_address,
        body: message,
        provider: providers.hasChoice ? providers.preferred : undefined,
      })
      // No WhatsApp provider configured: the backend hands back a wa.me link so
      // the send finishes in WhatsApp Web. Open it in a new tab for the user.
      if (data?.whatsapp_web_url) {
        window.open(data.whatsapp_web_url, "_blank", "noopener,noreferrer")
        toast({ title: "Opening WhatsApp Web", description: `Finish sending to ${resolved.to_address} in WhatsApp.` })
      } else {
        toast({ title: "Message queued", description: `Sent via ${channelMeta.label || channel} to ${resolved.to_address}.` })
      }
      onClose()
    } catch (e) {
      const errs = e?.response?.data?.errors
      toast({ variant: "destructive", title: "Failed to send", description: errs?.join(", ") })
    } finally {
      setSending(false)
    }
  }

  // Why the send is blocked, for inline messaging.
  const blockReason =
    category === "contact" && lead && !lead.contact ? "This lead has no linked contact." :
    category === "property" && !selectedProperty ? "Select a property to continue." :
    !templateId ? null : // no template chosen yet — button just disabled
    null

  const canSend = !!templateId && !!resolved && !sending && !previewing

  if (!lead) return null

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {ChannelIcon && <ChannelIcon className="h-4 w-4" />}
            Send {channelMeta.label || channel} message
          </SheetTitle>
          <SheetDescription>
            Lead: <span className="font-medium text-slate-700">{lead.name}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {/* Category tabs */}
          <div>
            <p className="mb-2 text-[13px] font-medium text-slate-700">Template type</p>
            <Tabs value={category} onChange={setCategory} />
          </div>

          {/* Property picker — Property tab only */}
          {category === "property" && (
            <div>
              <p className="mb-1.5 text-[13px] font-medium text-slate-700">Select property</p>
              <Select value={propertyId} onValueChange={setPropertyId}>
                <SelectTrigger><SelectValue placeholder="Choose a property listing" /></SelectTrigger>
                <SelectContent>
                  {properties.length === 0 && (
                    <div className="px-3 py-2 text-[13px] text-slate-400">No active listings.</div>
                  )}
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.title}{p.locality ? ` — ${p.locality}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Template dropdown */}
          <div>
            <p className="mb-1.5 text-[13px] font-medium text-slate-700">Select template</p>
            <Select value={templateId} onValueChange={setTemplateId} disabled={templatesLoading}>
              <SelectTrigger>
                <SelectValue placeholder={templatesLoading ? "Loading…" : "Select a template"} />
              </SelectTrigger>
              <SelectContent>
                {!templatesLoading && templates.length === 0 && (
                  <div className="px-3 py-2 text-[13px] text-slate-400">
                    No active {channelMeta.label || channel} templates for {category}.
                  </div>
                )}
                {templates.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message preview / body */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-[13px] font-medium text-slate-700">Message</p>
              {previewing && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            </div>
            <Textarea
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={templateId ? "Rendering…" : "Choose a template to populate the message."}
              className="text-[13px]"
            />
            {resolved?.to_address && (
              <p className="mt-1.5 text-[12px] text-slate-400">
                Sending to <span className="text-slate-600">{resolved.to_address}</span>
              </p>
            )}
            {blockReason && (
              <p className="mt-1.5 text-[12px] text-amber-600">{blockReason}</p>
            )}
          </div>
        </div>

        <SheetFooter>
          {providers.hasChoice && (
            <ProviderPicker
              providers={providers.providers}
              value={providers.preferred}
              onChange={providers.remember}
              label="Send via"
            />
          )}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={!canSend} className="gap-1.5">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send message
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
